import classNames from 'classnames'
import styles from './Slider.module.scss'

import { useState, useEffect, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
}

const swipeConfidenceThreshold = 10000
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity
}

export default function Slider({ slideElements = [], initialSlide = 0 }) {
  const [[page, direction], setPage] = useState([initialSlide, 0])
  const slideIndex = wrap(0, slideElements.length, page) //! page is 1...999, index wraps
  const Slide = slideElements[slideIndex]

  const paginate = useCallback(
    (newDirection) => {
      setPage([page + newDirection, newDirection])
    },
    [setPage, page],
  )

  // TODO revisit
  const navigate = (newPage) => {
    setPage([newPage, newPage < page ? -1 : 1])
  }

  useEffect(() => {
    const keyboardNavHandler = (e) => {
      if (e.key === 'ArrowLeft') paginate(-1)
      if (e.key === 'ArrowRight') paginate(1)
    }
    window.addEventListener('keydown', keyboardNavHandler)
    return () => {
      window.removeEventListener('keydown', keyboardNavHandler)
    }
  }, [paginate])

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.contentContainer}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            className={styles.page}
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 60 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x)
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1)
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1)
              }
            }}
          >
            {Slide}
          </motion.div>
        </AnimatePresence>
        <button
          className={styles.next}
          onClick={() => paginate(1)}
          aria-label="Next"
        >
          {'‣'}
        </button>
        <button
          className={styles.prev}
          onClick={() => paginate(-1)}
          aria-label="Previous"
        >
          {'‣'}
        </button>
      </div>
      <div className={styles.pipsContainer}>
        {slideElements.map((_, i) => (
          <button
            className={classNames(styles.pip, {
              [styles.pipActive]: i === slideIndex,
            })}
            onClick={() => navigate(i)}
            key={i}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export const wrap = (min, max, v) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}
